import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import websocketService from '../../services/websocket.js'

// Async thunk to connect to WebSocket
export const connectWebSocket = createAsyncThunk(
  'websocket/connect',
  async (url, { rejectWithValue, dispatch }) => {
    try {
      await websocketService.connect(url)
      
      // Set up event listeners
      websocketService.addEventListener('counter_update', (data) => {
        dispatch(receiveCounterUpdate(data))
      })
      
      websocketService.addEventListener('close', (data) => {
        dispatch(setConnectionStatus('disconnected'))
      })
      
      websocketService.addEventListener('error', (data) => {
        dispatch(setError(data.error?.message || 'WebSocket error'))
      })
      
      return { status: 'connected', url }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// Async thunk to disconnect WebSocket
export const disconnectWebSocket = createAsyncThunk(
  'websocket/disconnect',
  async (_, { rejectWithValue }) => {
    try {
      websocketService.disconnect()
      return { status: 'disconnected' }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// Async thunk to send WebSocket message
export const sendWebSocketMessage = createAsyncThunk(
  'websocket/sendMessage',
  async (message, { rejectWithValue }) => {
    try {
      websocketService.send(message)
      return { sent: true, message }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

const websocketSlice = createSlice({
  name: 'websocket',
  initialState: {
    connectionStatus: 'disconnected', // 'disconnected', 'connecting', 'connected', 'error'
    url: null,
    error: null,
    lastMessage: null,
    reconnectAttempts: 0,
  },
  reducers: {
    setConnectionStatus: (state, action) => {
      state.connectionStatus = action.payload
      if (action.payload === 'connected') {
        state.error = null
        state.reconnectAttempts = 0
      }
    },
    setError: (state, action) => {
      state.error = action.payload
      state.connectionStatus = 'error'
    },
    clearError: (state) => {
      state.error = null
    },
    receiveCounterUpdate: (state, action) => {
      state.lastMessage = {
        type: 'counter_update',
        data: action.payload,
        timestamp: new Date().toISOString()
      }
    },
    incrementReconnectAttempts: (state) => {
      state.reconnectAttempts += 1
    },
    resetReconnectAttempts: (state) => {
      state.reconnectAttempts = 0
    },
  },
  extraReducers: (builder) => {
    builder
      // Connect WebSocket
      .addCase(connectWebSocket.pending, (state, action) => {
        state.connectionStatus = 'connecting'
        state.error = null
        state.url = action.meta.arg || 'ws://localhost:8000/ws/counter/'
      })
      .addCase(connectWebSocket.fulfilled, (state, action) => {
        state.connectionStatus = 'connected'
        state.url = action.payload.url
        state.error = null
      })
      .addCase(connectWebSocket.rejected, (state, action) => {
        state.connectionStatus = 'error'
        state.error = action.payload
      })
      // Disconnect WebSocket
      .addCase(disconnectWebSocket.fulfilled, (state) => {
        state.connectionStatus = 'disconnected'
        state.url = null
        state.lastMessage = null
        state.error = null
      })
      // Send WebSocket message
      .addCase(sendWebSocketMessage.rejected, (state, action) => {
        state.error = action.payload
      })
  },
})

export const {
  setConnectionStatus,
  setError,
  clearError,
  receiveCounterUpdate,
  incrementReconnectAttempts,
  resetReconnectAttempts,
} = websocketSlice.actions

export default websocketSlice.reducer